import { useMemo, useState, useRef, useEffect } from 'react';
import { useBestFitContextMenu } from './useBestFitContextMenu';
import { getBrokerColorClass } from './OrderBook';

interface OrderRow {
  queue: number;
  orderNo: string;
  time: string;
  stockCode: string;
  buySell: string;
  board: string;
  price: number;
  release: number;
  open: number;
  foreignDomestic: string;
  brokerCode: string;
  message: string;
  timeValue: number;
}

export function BuyOrders({ currentTime, allOrders, isPlaying, onTimeChange, ignoreTimeFilter, showBroker = true, filter }: { currentTime: number; allOrders: any[]; isPlaying?: boolean; onTimeChange?: (time: number) => void; ignoreTimeFilter?: boolean; showBroker?: boolean; filter?: { price: string; broker: string; message: string } }) {
  const [sortConfig, setSortConfig] = useState<{ key: keyof OrderRow; direction: 'asc' | 'desc' } | null>({ key: 'timeValue', direction: 'desc' });
  const containerRef = useRef<HTMLDivElement>(null);
  const [scrollTop, setScrollTop] = useState(0);
  const [containerHeight, setContainerHeight] = useState(0);

  useEffect(() => {
    if (filter?.message === 'Open') {
      setSortConfig({ key: 'timeValue', direction: 'asc' });
    } else {
      setSortConfig({ key: 'timeValue', direction: 'desc' });
    }
  }, [filter?.message]);

  useEffect(() => {
    const updateHeight = () => {
      if (containerRef.current) setContainerHeight(containerRef.current.offsetHeight);
    };
    updateHeight();
    window.addEventListener('resize', updateHeight);
    return () => window.removeEventListener('resize', updateHeight);
  }, []);





  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => setScrollTop(e.currentTarget.scrollTop);

  const handleWheel = (e: React.WheelEvent<HTMLDivElement>) => {
    const el = containerRef.current;
    if (!el || !onTimeChange || e.deltaY <= 0) return;
    const atBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 5;
    if (atBottom) {
      const nextTime = allOrders.find(o => o.timeValue > currentTime)?.timeValue;
      if (nextTime !== undefined) {
        e.preventDefault();
        onTimeChange(nextTime);
      }
    }
  };

  const { isBestFit, handleContextMenu, ContextMenu } = useBestFitContextMenu();

  // Compute fit-content widths in px from full dataset.
  const colWidths = useMemo(() => {
    let maxOrd = 3, maxPriceVal = 0, maxRlsVal = 0, maxOpenVal = 0, maxMsg = 4;
    let maxTime = 4, maxInv = 3, maxBr = 2;

    for (const o of allOrders) {
      if (o.price > maxPriceVal) maxPriceVal = o.price;
      if (o.release > maxRlsVal) maxRlsVal = o.release;
      if (o.open !== undefined && o.open > maxOpenVal) maxOpenVal = o.open;
      
      const ordLen = o.orderNo ? String(o.orderNo).length : 0;
      if (ordLen > maxOrd) maxOrd = ordLen;
      
      const msgLen = o.message ? String(o.message).length : 0;
      if (msgLen > maxMsg) maxMsg = msgLen;

      if (isBestFit) {
        const timeLen = o.time ? String(o.time).length : 0;
        if (timeLen > maxTime) maxTime = timeLen;
        
        const invLen = o.foreignDomestic ? String(o.foreignDomestic).length : 0;
        if (invLen > maxInv) maxInv = invLen;
        
        const brLen = o.brokerCode ? String(o.brokerCode).length : 0;
        if (brLen > maxBr) maxBr = brLen;
      }
    }

    const maxPrice = Math.max(maxPriceVal > 0 ? maxPriceVal.toLocaleString().length : 1, 6); // "Price "
    const maxRls   = Math.max(maxRlsVal > 0 ? Math.round(maxRlsVal).toLocaleString().length : 1, 4); // "Rls "
    const maxOpen  = Math.max(maxOpenVal > 0 ? Math.round(maxOpenVal).toLocaleString().length : 1, 5); // "Open "
    maxOrd = Math.max(maxOrd, 4); // "Ord "
    maxTime = Math.max(maxTime, 5); // "Time "
    maxInv = Math.max(maxInv, 3); // "Inv"
    maxBr = Math.max(maxBr, 2); // "Br"
    maxMsg = Math.max(maxMsg, 6); // "Status"
    
    // When isBestFit is true, give exact px multiplier
    const px    = (n: number) => isBestFit ? `${Math.ceil(n * 8.5) + 12}px` : `${Math.ceil(n * 8.5) + 6}px`;
    const pxTxt = (n: number) => isBestFit ? `${Math.ceil(n * 8.5) + 16}px` : `${Math.ceil(n * 6.5) + 6}px`;

    return { 
      ord: `calc(${px(maxOrd)} + 13px)`, 
      price: isBestFit ? px(maxPrice) : '48px', 
      rls: px(maxRls), 
      open: px(maxOpen), 
      msg: isBestFit ? pxTxt(maxMsg) : undefined, // If undefined, we use flex:1
      time: isBestFit ? px(maxTime) : '66px',
      inv: isBestFit ? px(maxInv) : '22px',
      br: isBestFit ? px(maxBr) : '28px',
    };
  }, [allOrders, isBestFit]);

  const baseData = useMemo(() => {
    return allOrders.filter(row => row.buySell === 'B').map((row, index) => ({ ...row, _origIdx: index }));
  }, [allOrders]);

  const visibleData = useMemo(() => {
    const orderMap = new Map<string, any>();
    
    for (const o of baseData) {
      if (o.timeValue > currentTime) break;
      
      const existing = orderMap.get(o.orderNo);
      if (!existing) {
        orderMap.set(o.orderNo, { ...o });
      } else {
        existing.open = o.open;
        existing.message = o.message;
        if (o.message === 'Withdrawn' || o.message === 'Withdrawn Match' || o.message === 'Full Matched') {
          existing.open = 0;
        }
        // Partial Matched with open=0 means fully consumed — treat as Full Matched
        if (o.message === 'Partial Matched' && existing.open <= 0) {
          existing.open = 0;
          existing.message = 'Full Matched';
        }
      }
    }

    let filtered = Array.from(orderMap.values());
    
    if (filter) {
      filtered = filtered.filter(o => {
        if (filter.price && String(o.price) !== filter.price) return false;
        if (filter.broker && !String(o.brokerCode).toLowerCase().includes(filter.broker.toLowerCase())) return false;
        if (filter.message) {
          if (filter.message === 'Open') {
            if (o.message !== 'Open' && o.message !== 'Partial Matched') return false;
          } else {
            if (o.message !== filter.message) return false;
          }
        }
        return true;
      });
    }
    if (filtered.length > 5000) {
      filtered = filtered.slice(-5000);
    }
    
    if (sortConfig) {
      filtered.sort((a, b) => {
        const aValue = a[sortConfig.key];
        const bValue = b[sortConfig.key];
        if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
        if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;

        if (sortConfig.key === 'time' || sortConfig.key === 'timeValue') {
          // Secondary sort: Largest order number first (descending) because larger = matched later
          const aOrd = Number(a.orderNo) || Infinity;
          const bOrd = Number(b.orderNo) || Infinity;
          if (aOrd !== bOrd) {
            return sortConfig.direction === 'asc' ? aOrd - bOrd : bOrd - aOrd;
          }
        }

        // Tertiary sort: Smallest 'open' quantity first (0 = Full Matched at the top)
        return a.open - b.open;
      });
    } else {
      // Default: match data order but newest first
      filtered.reverse();
    }
    if (filtered.length > 5000) {
      filtered = filtered.slice(0, 5000);
    }
    return filtered;
  }, [baseData, currentTime, sortConfig, ignoreTimeFilter, filter]);

  const rowHeight = 21;
  const totalHeight = visibleData.length * rowHeight;
  const contentHeight = Math.max(containerHeight, totalHeight);

  // When time changes (e.g. new data arrives or timeline scrubbed), we ensure we stay at the top to see the newest data.
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    requestAnimationFrame(() => {
      if (!containerRef.current) return;
      // We only care about staying at the top since newest is always index 0
      containerRef.current.scrollTop = 0;
      setScrollTop(0);
    });
  }, [currentTime]);

  // Memory management: render exactly 400 rows max around the viewport
  const renderCount = 400;
  const currentIndex = Math.max(0, Math.floor(scrollTop / rowHeight));
  let startIndex = Math.max(0, currentIndex - Math.floor(renderCount / 2));
  let endIndex = Math.min(visibleData.length, startIndex + renderCount);

  if (endIndex - startIndex < renderCount && visibleData.length > renderCount) {
    startIndex = endIndex - renderCount;
  }

  const handleSort = (key: keyof OrderRow) => {
    setSortConfig(prev => {
      if (!prev || prev.key !== key) return { key, direction: 'asc' };
      if (prev.direction === 'asc') return { key, direction: 'desc' };
      return null; // desc → null (no sort)
    });
  };

  return (
    <div className={`flex flex-col h-full bg-black border-r border-gray-800 relative ${isBestFit ? 'overflow-x-auto' : 'overflow-hidden'}`}>
      <ContextMenu />
      <div className={`flex bg-gray-900 text-gray-400 text-[13px] font-bold border-b border-gray-700 cursor-pointer select-none py-0.5 z-10 pr-[6px] whitespace-nowrap ${isBestFit ? 'w-full' : ''}`} onContextMenu={handleContextMenu}>
        <div onClick={() => handleSort('orderNo')} className="px-1 text-center" style={{width: colWidths.ord}}>Ord</div>
        <div onClick={() => handleSort('time')} className="px-1 text-center flex items-center justify-center gap-0.5" style={{width: colWidths.time}}>
          Time<span className="text-[10px]">{sortConfig?.key === 'time' ? (sortConfig.direction === 'asc' ? ' ↑' : ' ↓') : <span className="text-gray-600"> ↕</span>}</span>
        </div>
        <div onClick={() => handleSort('price')} className="px-1 text-center flex items-center justify-center gap-0.5" style={{width: colWidths.price}}>
          Price<span className="text-[10px]">{sortConfig?.key === 'price' ? (sortConfig.direction === 'asc' ? ' ↑' : ' ↓') : <span className="text-gray-600"> ↕</span>}</span>
        </div>
        <div onClick={() => handleSort('release')} className="px-1 text-center" style={{width: colWidths.rls}}>Rls</div>
        <div onClick={() => handleSort('open')} className="px-1 text-center" style={{width: colWidths.open}}>Open</div>
        <div onClick={() => handleSort('foreignDomestic')} className="px-1 text-center" style={{width: colWidths.inv}}>Inv</div>
        {showBroker && <div onClick={() => handleSort('brokerCode')} className="px-1 text-center" style={{width: colWidths.br}}>Br</div>}
        <div onClick={() => handleSort('message')} className="pl-[10px] pr-1 text-left" style={{width: colWidths.msg}}>Status</div>
        {isBestFit && <div className="flex-1"></div>}
      </div>

      <div ref={containerRef} className={`flex-1 overflow-y-auto scrollbar-thin scrollbar-thumb-gray-700 relative ${isBestFit ? 'w-full' : ''}`} onScroll={handleScroll} onWheel={handleWheel}>
        <div style={{ height: contentHeight, width: '100%', willChange: 'transform' }}>
          <div style={{ transform: `translate3d(0, ${startIndex * rowHeight}px, 0)`, willChange: 'transform' }}>
            {visibleData.slice(startIndex, endIndex).map((order, idx) => (
              <div key={startIndex + idx} className={`flex hover:bg-gray-900 text-[13px] tabular-nums items-center h-[21px] overflow-hidden`}>
                <div className="px-1 text-center text-gray-300 truncate" style={{width: colWidths.ord}}>{order.orderNo}</div>
                <div className="px-1 text-center text-gray-400 truncate" style={{width: colWidths.time}}>{order.time}</div>
                <div className="px-1 text-right text-green-500 truncate" style={{width: colWidths.price}}>{order.price.toLocaleString()}</div>
                <div className="px-1 text-right text-gray-400 truncate" style={{width: colWidths.rls}}>{order.release ? order.release.toLocaleString() : ''}</div>
                <div className="px-1 text-right text-gray-300 truncate" style={{width: colWidths.open}}>{order.open.toLocaleString()}</div>
                <div className="px-1 text-center text-gray-400 truncate" style={{width: colWidths.inv}}>{order.foreignDomestic}</div>
                {showBroker && <div className={`px-1 text-center truncate ${getBrokerColorClass(order.brokerCode)}`} style={{width: colWidths.br}}>{order.brokerCode}</div>}
                <div className={`pl-[10px] pr-[10px] truncate ${
                  order.message === 'Full Matched' ? 'text-green-500' :
                  order.message === 'Partial Matched' ? 'text-green-400' :
                  order.message === 'Open' ? 'text-yellow-500' :
                  order.message === 'Amended' ? 'text-cyan-500' : 'text-red-500'
                }`} style={{width: colWidths.msg}}>{order.message}</div>
                {isBestFit && <div className="flex-1"></div>}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}


